import { useState, useEffect } from "react";
import { Form, Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ReactComponent as PencilSvg } from "@/assets/images/desktop/editar.svg";
import { ReactComponent as RemoveSvg } from "@/assets/images/desktop/remove.svg";
import { ReactComponent as ChileSvg } from "@/assets/images/desktop/flags/chile.svg";
import { ReactComponent as UruguaySvg } from "@/assets/images/desktop/flags/uruguay.svg";
import { ReactComponent as StockSvg } from "@/assets/images/desktop/alert-critical.svg";
import { ReactComponent as MovementSvg } from "@/assets/images/desktop/movements.svg";

import { unitMeasurementOptions } from "@/pages/Maintainers/Products/products.constants.jsx";
import CustomToggle from "@/components/Dropdown/CustomToggle";
import EditMultiple from "@/components/Table/EditMultiple/EditMultiple";
import EditSingle from "@/components/Table/EditSingle/EditSingle";
import EditText from "@/components/Table/EditText/EditText.jsx";
import CustomInputSwitch from "@/components/desktop/form/input/input-switch/CustomInputSwitch";

import classes from "../maintainers.module.scss";

import { formatMilesDecimal, formatMilesDecimalNew, formatUnitMeasure } from "@/services/Utils";
import api from "@/services/api.service";

const APPROVAL_ROW_STYLE = { backgroundColor: "#fff3cd", color: "#856404" };
const ADMIN_APPROVAL_ROW_STYLE = { backgroundColor: "#f2d1d0", color: "red" };

const ProductTableItem = ({
	_i,
	ListItem,
	selectProduct,
	handleDelete,
	filter,
	checked,
	groupProducts,
	localeProducts,
	onJoinProduct,
	handlePriceClick,
	typeCurrency,
	selectedEstatesParentsCount,
}) => {
	const location = useLocation();
	const { section } = useParams();
	const navigate = useNavigate();

	const isMaintainerView = section === undefined;
	const params = new URLSearchParams(location.search);
	const filterCategory = params.get("filterCategory");

	const { categories, aptitudes, active_ingredients } = useSelector((state) => state.maintainers_products);

	const [item, setItem] = useState({});
	const [localAptitudes, setLocalAptitudes] = useState([]);
	const [localActiveIngredients, setLocalActiveIngredients] = useState([]);

	useEffect(() => {
		if (localAptitudes.length === 0) {
			setLocalAptitudes(aptitudes);
		}
		if (localActiveIngredients.length === 0) {
			setLocalActiveIngredients(active_ingredients);
		}
	}, [aptitudes, active_ingredients, localActiveIngredients.length, localAptitudes.length]);

	useEffect(() => {
		setItem(ListItem);
	}, [ListItem]);

	const handleChangeCategory = async (value) => {
		await api.post("/admin/products/update/categories", { id: item.id, category_id: value }).then((response) => {
			const categoryNames = response.category_name.join(", ");
			setItem({ ...item, category_name: categoryNames });

			toast.success(`Categoría actualizada correctamente`);
		});
	};

	const handleChangeAptitude = async (value) => {
		const URL = isMaintainerView ? "/admin/products/update/aptitudes" : "/admin/products/update/aptitudes/custom";

		await api.post(URL, { product_id: item.id, aptitude: value }).then((response) => {
			setItem({ ...item, aptitud_name: response.text_aptitudes });

			toast.success(`Aptitud actualizada correctamente`);
		});
	};

	const handleChangeActiveIngredient = async (value) => {
		const URL = isMaintainerView ? "/admin/products/update/active_ingredients" : "/admin/products/update/active_ingredients/custom";

		await api.post(URL, { product_id: item.id, active_ingredient: value }).then((response) => {
			setItem({ ...item, active_ingredient: response.text_active_ingredients });
			toast.success(`Ingrediente activo actualizado correctamente`);
		});
	};

	const handleChangeProductCount = async (value) => {
		setItem({ ...item, product_count: value });
		await api.post("/admin/products/update/product_count", { id: item.id, product_count: value }).then(() => {
			toast.success(`Producto actualizado correctamente`);
		});
	};

	const handleChangeTradename = async (value) => {
		const response = await api.post("/admin/products/update/tradename", { id: item.id, tradename: value });

		if (response?.response?.status === 400 || response?.isAxiosError) {
			const errorMessage = response?.response?.data?.data || "Ha ocurrido un error al actualizar el nombre";
			toast.error(errorMessage, { toastId: `tradename-error-${item.id}` });
			return;
		}

		setItem({ ...item, tradename: value });
		toast.success(`Nombre actualizado correctamente`, { toastId: `tradename-success-${item.id}` });
	};

	const handleJoinProduct = async (extraParams) => {
		const response = await onJoinProduct([item], extraParams);

		if (response?.response?.status === 400 || response?.isAxiosError) {
			const errorMessage = response?.response?.data?.data || "Ha ocurrido un error al juntar los productos";
			toast.error(errorMessage, { toastId: `join-error-${item.id}` });
		}
	};

	const handleChangeUnitMeasurement = async (value) => {
		const response = await api.post("/admin/products/update/unit_measurement", { id: item.id, unit_measurement: value });

		if (response?.response?.status === 400 || response?.isAxiosError) {
			const errorMessage = response?.response?.data?.data || "Ha ocurrido un error al actualizar la unidad de medida";
			toast.error(errorMessage, { toastId: `unit-measurement-error-${item.id}` });
			return;
		}

		setItem({ ...item, unit_measurement: value });
		toast.success(`Unidad de medida actualizada correctamente`, { toastId: `unit-measurement-success-${item.id}` });
	};

	const renderTradenameCell = () => {
		if (isMaintainerView) return item.tradename;

		if (!item.generic) {
			return (
				<EditSingle
					options={localeProducts}
					handleChange={(destinationId) => handleJoinProduct({ product_destination: destinationId })}
					onCreateOption={(tradename) => handleJoinProduct({ tradename })}
					currentValue={item.id}
					currentLabel={item.tradename}
				/>
			);
		}

		return <EditText handleChange={handleChangeTradename} currentValue={item.tradename} currentLabel={item.tradename} />;
	};

	const renderPriceCell = () => {
		let styles = classes.price_manual;
		let price = 0;

		if (item.prices && item.prices.length > 0) {
			const pricesWithManual = item.prices.filter((p) => p.is_manual).length;
			const allAreManual = pricesWithManual === item.prices.length;
			const hasManual = pricesWithManual > 0;
			const allHavePrice = item.prices.length === (item.generic && item.estate_id ? 1 : selectedEstatesParentsCount);

			price = item.prices.reduce((sum, p) => sum + (typeCurrency === "USD" ? Number(p.price_usd) : Number(p.price)), 0) / item.prices.length;

			if (allAreManual) {
				styles = classes.price_manual;
			} else if (allHavePrice && !hasManual) {
				styles = classes.price;
			} else {
				styles = classes.price_combined;
			}
		}

		const formatPrice = price > 0 ? `$${formatMilesDecimal(price)}` : `$0`;

		return (
			<span className={styles} onClick={() => handlePriceClick(item)} title="Click para ver detalles del precio">
				{formatPrice}
			</span>
		);
	};

	const shouldShowColumn = (key, { isMaintainerView, filter }) => {
		const { is_generic, customer_id } = filter;

		if (isMaintainerView) {
			const hiddenForMaintainer = ["precio"];
			return !hiddenForMaintainer.includes(key);
		}

		if (!isMaintainerView) {
			const allowedIfTrue = ["checkbox", "tradename", "active_ingredient", "aptitud", "unit_measurement", "actions", "precio"];
			const allowedIfFalse = [
				"checkbox",
				"tradename",
				"active_ingredient",
				"aptitud",
				"bodega",
				"inventory_code",
				"stock",
				"unit_measurement",
				"product_count",
				"actions",
				"precio",
			];
			return is_generic ? allowedIfFalse.includes(key) : allowedIfTrue.includes(key);
		}

		if (!is_generic && !customer_id) {
			const hiddenForAnonymous = ["customer", "bodega", "inventory_code", "stock", "product_count"];
			return !hiddenForAnonymous.includes(key);
		}

		return true;
	};

	const allColumns = [
		{
			key: "checkbox",
			className: "center",
			size: 5,
			content: (
				<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
					<Form.Check type="checkbox" checked={checked} onChange={() => selectProduct(item)} />
					{groupProducts[0] && groupProducts[0].id === item.id && (
						<span className="badge badge-info" style={{ fontSize: "6px" }}>
							1er elemento
						</span>
					)}
				</div>
			),
		},
		{
			key: "tradename",
			className: "long_text first_text editable",
			size: 15,
			content: renderTradenameCell(),
		},
		{
			key: "active_ingredient",
			className: "long_text center multiple editable",
			content: (
				<EditMultiple
					options={localActiveIngredients}
					setOptions={setLocalActiveIngredients}
					handleChange={handleChangeActiveIngredient}
					currentValue={item.active_ingredient_id}
					currentLabel={item.active_ingredient}
				/>
			),
		},
		{
			key: "aptitud",
			className: "long_text center multiple editable",
			content: (
				<EditMultiple
					options={localAptitudes}
					setOptions={setLocalAptitudes}
					handleChange={handleChangeAptitude}
					currentValue={item.aptitude_id}
					currentLabel={item.aptitud_name}
				/>
			),
		},
		{
			key: "density",
			className: "center",
			content: formatMilesDecimalNew(item.density),
		},
		{
			key: "category",
			className: "long_text center multiple editable",
			content: (
				<EditMultiple
					options={categories}
					setOptions={null}
					handleChange={handleChangeCategory}
					currentValue={item.category_id}
					currentLabel={item.category_name}
				/>
			),
		},
		{
			key: "country",
			className: "center",
			content: item.country_id === 1 ? <ChileSvg className={classes.icon_flags} /> : <UruguaySvg className={classes.icon_flags} />,
		},
		{
			key: "customer",
			className: "long_text center",
			content: item.customer,
		},
		{
			key: "bodega",
			className: "long_text center",
			content: item.estate,
		},
		{
			key: "precio",
			className: "long_text center",
			content: renderPriceCell(),
		},
		{
			key: "inventory_code",
			className: "long_text center",
			content: item.inventory_code,
		},
		{
			key: "stock",
			className: "long_text right",
			content: formatMilesDecimalNew(String(item.stock).replace(".", ",")),
		},
		{
			key: "unit_measurement",
			className: filter.is_generic ? "long_text center editable" : "long_text center",
			content: filter.is_generic ? (
				<EditSingle
					options={unitMeasurementOptions}
					handleChange={handleChangeUnitMeasurement}
					currentValue={item.unit_measurement}
					currentLabel={formatUnitMeasure(item.unit_measurement)}
					minChars={0}
				/>
			) : (
				formatUnitMeasure(item.unit_measurement)
			),
		},
		{
			key: "product_count",
			className: "long_text center",
			content: <CustomInputSwitch value={item.product_count} onChange={handleChangeProductCount} />,
		},
		{
			key: "actions",
			className: "last_div",
			content: (
				<div style={{ display: "flex", gap: "10px" }}>
					{isMaintainerView ? (
						<>
							<PencilSvg
								className={classes.icon}
								onClick={() =>
									navigate(`/mantenedores/productos/editar/${item.id}${filterCategory ? `?filterCategory=${filterCategory}` : ""}`)
								}
							/>
							<RemoveSvg className={classes.icon} onClick={() => handleDelete(item)} />
						</>
					) : (
						<Dropdown>
							<Dropdown.Toggle as={CustomToggle} id="dropdown-custom-components" />
							<Dropdown.Menu>
								<Dropdown.Item onClick={() => navigate(`${location.pathname}/movimientos/${item.code}/${item.inventory_id}`)}>
									<div style={{ display: "flex", alignItems: "center" }}>
										<MovementSvg style={{ marginRight: "8px", height: "1em", width: "1em" }} />
										Ver Movimientos
									</div>
								</Dropdown.Item>
								<Dropdown.Item onClick={() => navigate(`${location.pathname}/stock_critico/${item.code}`)}>
									<div style={{ display: "flex", alignItems: "center" }}>
										<StockSvg style={{ marginRight: "8px", height: "1em", width: "1em" }} />
										Ingresar Stock Crítico
									</div>
								</Dropdown.Item>
								{!!handleDelete && (
									<Dropdown.Item onClick={() => handleDelete(item)} className="text-danger">
										<div style={{ display: "flex", alignItems: "center" }}>
											<RemoveSvg style={{ marginRight: "8px", height: "1em", width: "1em" }} />
											Eliminar
										</div>
									</Dropdown.Item>
								)}
							</Dropdown.Menu>
						</Dropdown>
					)}
				</div>
			),
		},
	];

	const columns = allColumns.filter((col) => shouldShowColumn(col.key, { isMaintainerView, filter }));
	let rowStyle = {};

	if (item.need_approval) {
		rowStyle = APPROVAL_ROW_STYLE;
	} else if (item.need_approval_admin && isMaintainerView) {
		rowStyle = ADMIN_APPROVAL_ROW_STYLE;
	}

	return (
		<tr key={ListItem.id} className={classes.table_tr} style={rowStyle}>
			{columns.map(({ key, className, content }) =>
				content !== null ? (
					<td key={key} className={classes.table_td}>
						<div className={[classes.table_div, ...className.split(" ").map((c) => classes[c])].join(" ")}>{content}</div>
					</td>
				) : null,
			)}
		</tr>
	);
};

export default ProductTableItem;
